import type { RunGroup, RunInstance } from "@skaile/workspaces/types";

const priorityOf = (instance: RunInstance): number => instance.input.priority ?? 0;

/**
 * Which queued instances may spawn now. Ordered by priority descending, then
 * FIFO by receivedAt (instanceId tiebreak), bounded by maxParallel minus
 * slot-occupying instances. Slot-occupying = spawning | running. Gate-parked
 * instances (awaiting_approval / awaiting_input) do NOT hold slots - hosts
 * hibernate them, so a slow approver cannot stall intake.
 *
 * An absent priority is 0, so a queue of inputs that never set one keeps the
 * exact FIFO order it had before priority existed.
 *
 * receivedAt must be UTC ISO 8601 (Z-suffixed): ordering is lexicographic,
 * so non-UTC offsets would misorder the queue.
 */
export function admit(group: RunGroup, instances: RunInstance[]): RunInstance[] {
  if (group.status !== "active") return [];
  const occupying = instances.filter(
    (i) => i.status === "spawning" || i.status === "running",
  ).length;
  const slots = Math.max(0, group.policy.maxParallel - occupying);
  if (slots === 0) return [];
  return instances
    .filter((i) => i.status === "queued")
    .sort(
      (a, b) =>
        priorityOf(b) - priorityOf(a) ||
        a.input.receivedAt.localeCompare(b.input.receivedAt) ||
        a.instanceId.localeCompare(b.instanceId),
    )
    .slice(0, slots);
}
